import { SCHEMA_VERSION } from '../../constants/app.constants';
import { AppState } from '../../models/app-state.model';
import { validateAppState } from '../../validators/app-state.validator';
import { ValidationResult } from '../../validators/validation-result.model';
import {
  mapPersistedAppStateToRuntime,
  parsePersistedAppState,
} from '../mappers/persisted-app-state.mapper';
import { preserveLegacyPersistedAppState } from '../legacy/persisted-legacy-preservation';
import {
  CurrentPersistedAppState,
  PersistedAppState,
  SupportedPersistedSchemaVersion,
} from '../schemas/persisted-app-state.model';
import { normalizePersistedState } from '../normalizers/persisted-app-state.normalizer';
import { migratePersistedAppStateV1ToV2 } from './v1-to-v2.migrator';

export interface LoadedPersistedAppStateResult extends ValidationResult {
  parsedState: AppState | null;
  currentPersistedState: CurrentPersistedAppState | null;
  detectedSchemaVersion: number | null;
  migratedFromVersion: SupportedPersistedSchemaVersion | null;
  wasNormalized: boolean;
}

const SUPPORTED_PERSISTED_SCHEMA_VERSIONS: readonly SupportedPersistedSchemaVersion[] = [1, 2];

export function detectPersistedSchemaVersion(candidate: unknown): number | null {
  if (candidate === null || typeof candidate !== 'object') {
    return null;
  }

  const schemaVersion = (candidate as Record<string, unknown>)['schemaVersion'];

  return typeof schemaVersion === 'number' ? schemaVersion : null;
}

export function isSupportedPersistedSchemaVersion(version: number): version is SupportedPersistedSchemaVersion {
  return SUPPORTED_PERSISTED_SCHEMA_VERSIONS.includes(version as SupportedPersistedSchemaVersion);
}

export function migratePersistedAppStateToCurrent(
  persistedState: PersistedAppState,
): CurrentPersistedAppState {
  let currentState: PersistedAppState = persistedState;

  while (currentState.schemaVersion !== SCHEMA_VERSION) {
    const schemaVersion = currentState.schemaVersion;

    switch (schemaVersion) {
      case 1:
        currentState = migratePersistedAppStateV1ToV2(currentState);
        break;
      default:
        throw new Error(
          `No migrator is registered for persisted schema version ${schemaVersion}.`,
        );
    }
  }

  return currentState;
}

export function loadPersistedAppState(candidate: unknown): LoadedPersistedAppStateResult {
  const detectedSchemaVersion = detectPersistedSchemaVersion(candidate);

  if (detectedSchemaVersion === null) {
    return {
      isValid: false,
      errors: ['Persisted data is missing a numeric schemaVersion.'],
      parsedState: null,
      currentPersistedState: null,
      detectedSchemaVersion: null,
      migratedFromVersion: null,
      wasNormalized: false,
    };
  }

  if (!isSupportedPersistedSchemaVersion(detectedSchemaVersion)) {
    return {
      isValid: false,
      errors: [
        `Persisted schema version ${detectedSchemaVersion} is not supported. Supported versions: ${SUPPORTED_PERSISTED_SCHEMA_VERSIONS.join(', ')}.`,
      ],
      parsedState: null,
      currentPersistedState: null,
      detectedSchemaVersion,
      migratedFromVersion: null,
      wasNormalized: false,
    };
  }

  const persistedState = parsePersistedAppState(candidate);

  if (!persistedState) {
    return {
      isValid: false,
      errors: [`Persisted data does not match schema version ${detectedSchemaVersion}.`],
      parsedState: null,
      currentPersistedState: null,
      detectedSchemaVersion,
      migratedFromVersion: null,
      wasNormalized: false,
    };
  }

  let migratedState: CurrentPersistedAppState;

  try {
    migratedState = migratePersistedAppStateToCurrent(persistedState);
    migratedState = preserveLegacyPersistedAppState(migratedState);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Migration failed unexpectedly.';

    return {
      isValid: false,
      errors: [message],
      parsedState: null,
      currentPersistedState: null,
      detectedSchemaVersion,
      migratedFromVersion: null,
      wasNormalized: false,
    };
  }

  let normalizationResult;
  let runtimeState;
  let validationResult;

  try {
    normalizationResult = normalizePersistedState(migratedState);
    runtimeState = mapPersistedAppStateToRuntime(normalizationResult.state);
    validationResult = validateAppState(runtimeState);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Persisted data could not be validated safely.';

    return {
      isValid: false,
      errors: [message],
      parsedState: null,
      currentPersistedState: null,
      detectedSchemaVersion,
      migratedFromVersion: null,
      wasNormalized: false,
    };
  }

  if (!validationResult.isValid) {
    return {
      isValid: false,
      errors: validationResult.errors,
      parsedState: null,
      currentPersistedState: null,
      detectedSchemaVersion,
      migratedFromVersion: null,
      wasNormalized: false,
    };
  }

  return {
    isValid: true,
    errors: [],
    parsedState: runtimeState,
    currentPersistedState: normalizationResult.state,
    detectedSchemaVersion,
    migratedFromVersion: detectedSchemaVersion === SCHEMA_VERSION ? null : detectedSchemaVersion,
    wasNormalized: normalizationResult.wasNormalized,
  };
}